import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  TextField,
  List,
  ListItem,
  ListItemText,
  Paper,
  Box,
  BoxProps,
  InputAdornment,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from '@mui/material/styles';
import { getAutoCompleteAddressInputStyles } from './AutoCompleteAddressInput.styles';
import {
  fetchPlaceAutocomplete,
  fetchPlaceDetails,
  isAllowedCountry,
  type PlaceAddressDetails as AddressDetails,
  type PlaceSuggestion as AddressSuggestion,
} from '@/utils/placesApi';

interface AutoCompleteAddressInputProps extends Omit<BoxProps, 'onChange'> {
  value: string;
  onChange: (address: string) => void;
  onAddressSelect: (addressDetails: AddressDetails) => void;
  placeholder?: string;
  disabled?: boolean;
  // Sets native required/aria-required; visible asterisk is on the caller's label.
  required?: boolean;
  error?: boolean;
  helperText?: string | null;
  name?: string;
  size?: 'small' | 'medium';
  // Optional clear callback. When provided, the field renders a small "x"
  // adornment at the right end whenever ``value`` is non-empty. Clicking
  // the adornment invokes the callback — useful for letting users wipe
  // the field without selecting-all + delete on touch devices. MUI's
  // ``endAdornment`` slot reserves space at the right edge so long
  // addresses scroll under the cursor, never under the icon.
  onClear?: () => void;
  // Accessible name for the input. Defaults to "Street address" if the
  // caller doesn't pass a more specific label (e.g. "Voting address").
  // The visible placeholder is not a reliable label per WCAG 2.5.3, so
  // an explicit aria-label always renders on the input.
  ariaLabel?: string;
  // Position of the suggestions dropdown relative to the input.
  // ``'below'`` (default) drops down beneath the field — the natural
  // choice when there's room. ``'above'`` flips it on top of the
  // field — required when the input sits near the bottom of the
  // viewport where the on-screen keyboard would otherwise obscure a
  // below-dropdown (e.g., the MobileAddressBar that docks above the
  // keyboard).
  dropdownDirection?: 'above' | 'below';
  // Forwarded to the underlying TextField's ``inputRef`` so callers
  // can programmatically focus the actual ``<input>`` element. Used
  // by ``MobileAddressBar`` to let the LeftPaneMenu's "My Ballot
  // Preview" item focus the address input in the no-address state.
  inputRef?: React.Ref<HTMLInputElement>;
  // Label for the on-screen keyboard's enter key (e.g. "go" inside a form).
  enterKeyHint?: React.InputHTMLAttributes<HTMLInputElement>['enterKeyHint'];
  // Reports how many suggestions the latest lookup returned (0 when cleared).
  onSuggestionsChange?: (count: number) => void;
  // Called when a suggestion is tapped, with the in-flight details lookup, so a
  // caller can await a selection the user already made instead of rejecting it.
  onSelectionPending?: (pending: Promise<AddressDetails | null>) => void;
  // Receives the message when a tapped suggestion can't be used (lookup failed
  // or non-US), or null when it clears. Without it the field shows the message
  // as its own helper text.
  onSelectionError?: (message: string | null) => void;
}

const AutoCompleteAddressInput: React.FC<AutoCompleteAddressInputProps> = ({
  value,
  onChange,
  onAddressSelect,
  placeholder,
  disabled = false,
  required = false,
  error,
  helperText,
  sx,
  id,
  size,
  onClear,
  ariaLabel,
  dropdownDirection = 'below',
  inputRef,
  enterKeyHint,
  onSuggestionsChange,
  onSelectionPending,
  onSelectionError,
}) => {
  const { t } = useTranslation('common');
  // Fall back to a localized generic label when the caller doesn't pass a
  // more specific accessible name. WCAG 2.5.3 — the placeholder is not a
  // reliable label, so an explicit aria-label always renders on the input.
  const resolvedAriaLabel = ariaLabel ?? t('streetAddressAriaLabel');
  const resolvedPlaceholder = placeholder ?? t('addressPlaceholder');
  const [suggestions, setSuggestions] = React.useState<AddressSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = React.useState(false);
  const [selectionError, setSelectionError] = React.useState<string | null>(null);
  // Keyboard-highlighted suggestion, indexed into ``displayed`` (visual order).
  const [highlighted, setHighlighted] = React.useState(-1);
  const listboxId = React.useId();
  const debounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  // Bumped on every edit, clear, and tap. A suggestions or details lookup
  // that returns after the field has moved on is ignored, so it can't
  // overwrite newer suggestions or text.
  const selectionGenRef = React.useRef(0);

  const reportSelectionError = (message: string | null) => {
    if (onSelectionError) onSelectionError(message);
    else setSelectionError(message);
  };

  const updateSuggestions = (next: AddressSuggestion[]) => {
    setSuggestions(next);
    setShowSuggestions(next.length > 0);
    setHighlighted(-1);
    onSuggestionsChange?.(next.length);
  };

  // Thin wrapper over the shared Places client that also drives the
  // dropdown's local state. Network/mapping logic lives in utils/placesApi.
  const runAutocomplete = async (inputText: string) => {
    const gen = selectionGenRef.current;
    const next = await fetchPlaceAutocomplete(inputText);
    if (gen === selectionGenRef.current) updateSuggestions(next);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    selectionGenRef.current += 1;
    onChange(newValue);
    if (selectionError) setSelectionError(null);

    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (newValue.length <= 1) {
      updateSuggestions([]);
      return;
    }

    debounceRef.current = setTimeout(() => runAutocomplete(newValue), 300);
  };

  const handleSuggestionSelect = async (suggestion: AddressSuggestion) => {
    setShowSuggestions(false);
    setHighlighted(-1);
    onChange(suggestion.description);
    const gen = ++selectionGenRef.current;

    const lookup = fetchPlaceDetails(suggestion.place_id);
    const pending = lookup.then((details) =>
      details && isAllowedCountry(details) ? details : null
    );
    onSelectionPending?.(pending);

    const details = await lookup;
    if (gen !== selectionGenRef.current) return;
    // Defensive: reject anything the backend filter let through that isn't
    // the United States or a US territory with US ballots. The tapped text
    // stays so the user can retry.
    if (!details || !isAllowedCountry(details)) {
      reportSelectionError(t(details ? 'addressNotSupported' : 'addressLookupFailed'));
      return;
    }

    reportSelectionError(null);
    onAddressSelect(details);
  };

  const handleInputFocus = () => {
    if (suggestions.length > 0) setShowSuggestions(true);
  };

  const handleInputBlur = () => {
    setTimeout(() => setShowSuggestions(false), 200);
  };

  const above = dropdownDirection === 'above';
  // Above the input, reverse so the closest match sits at the bottom,
  // nearest what the user is typing (#2510).
  const displayed = above ? [...suggestions].reverse() : suggestions;
  const listOpen = showSuggestions && displayed.length > 0;
  const optionId = (index: number) => `${listboxId}-option-${index}`;

  // Arrow keys walk the list in visual order starting from the item nearest
  // the input; Enter picks the highlighted item instead of submitting an
  // enclosing form, and Escape closes the list.
  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!listOpen) return;
    const last = displayed.length - 1;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const towardInput = above ? event.key === 'ArrowDown' : event.key === 'ArrowUp';
      setHighlighted((i) => {
        if (i === -1) return towardInput ? -1 : above ? last : 0;
        const next = towardInput === above ? i + 1 : i - 1;
        if (next < 0 || next > last) return towardInput ? -1 : i;
        return next;
      });
    } else if (event.key === 'Enter' && highlighted >= 0) {
      event.preventDefault();
      void handleSuggestionSelect(displayed[highlighted]);
    } else if (event.key === 'Escape') {
      setShowSuggestions(false);
      setHighlighted(-1);
    }
  };

  const theme = useTheme();
  const styles = getAutoCompleteAddressInputStyles(theme);

  return (
    <Box sx={styles.container}>
      <TextField
        fullWidth
        variant="outlined"
        size={size}
        value={value}
        inputRef={inputRef}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        onBlur={handleInputBlur}
        placeholder={resolvedPlaceholder}
        disabled={disabled}
        required={required}
        sx={{ ...styles.textField, ...sx }}
        error={error || Boolean(selectionError)}
        helperText={selectionError ?? (error && helperText ? helperText : undefined)}
        slotProps={{
          // Announced on appearance: focus stays in the input when a
          // suggestion is pressed, so no focus change would re-read it.
          formHelperText: { role: 'alert' },
          htmlInput: {
            enterKeyHint,
            onKeyDown: handleKeyDown,
            role: 'combobox',
            'aria-autocomplete': 'list',
            'aria-expanded': listOpen,
            'aria-controls': listOpen ? listboxId : undefined,
            'aria-activedescendant':
              listOpen && highlighted >= 0 ? optionId(highlighted) : undefined,
          },
          input: {
            id: id,
            'aria-label': resolvedAriaLabel,
            // Only render the clear button when the caller opted in AND the
            // field has content. Material convention: no "clear nothing"
            // target on empty inputs.
            endAdornment:
              onClear && value.length > 0 ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    aria-label={t('clearAddress')}
                    edge="end"
                    // Suppress the default mousedown focus shift so the input's
                    // blur doesn't fire first and steal focus to nowhere, and
                    // clear on click so the press stays cancellable (WCAG 2.5.2).
                    onMouseDown={(e) => {
                      e.preventDefault();
                    }}
                    onClick={() => {
                      selectionGenRef.current += 1;
                      onClear();
                    }}
                    sx={{ p: 0.25 }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
          },
        }}
      />

      {listOpen && (
        <Paper
          elevation={3}
          // Keep focus in the input while a suggestion is pressed: otherwise
          // the blur's 200 ms close can drop the list out from under a slow tap.
          onMouseDown={(e) => e.preventDefault()}
          sx={{
            ...styles.suggestionsPaper,
            // Anchor the dropdown to the chosen side of the input.
            // ``below`` (default) keeps the existing top-anchored
            // styling. ``above`` swaps to bottom-anchored so the
            // dropdown grows upward — used by the MobileAddressBar
            // where the keyboard would obscure a below-dropdown.
            ...(above ? { top: 'auto', bottom: '100%', marginTop: 0, marginBottom: '4px' } : {}),
          }}
        >
          <List dense id={listboxId} role="listbox" aria-label={resolvedAriaLabel}>
            {displayed.map((s, index) => (
              <ListItem
                key={s.place_id}
                id={optionId(index)}
                component="div"
                role="option"
                aria-selected={index === highlighted}
                onClick={() => handleSuggestionSelect(s)}
                onMouseEnter={() => setHighlighted(index)}
                sx={{
                  ...styles.suggestionItem,
                  ...(index === highlighted ? styles.suggestionItemHighlighted : {}),
                }}
              >
                <ListItemText
                  primary={s.structured_formatting?.main_text || s.description}
                  secondary={s.structured_formatting?.secondary_text}
                  primaryTypographyProps={styles.primaryText}
                  secondaryTypographyProps={styles.secondaryText}
                />
              </ListItem>
            ))}
          </List>
        </Paper>
      )}
    </Box>
  );
};

export default AutoCompleteAddressInput;
